import { useEffect, useState } from "react";

const GOOGLE_TRANSLATE_SCRIPT_ID = "google-translate-script";
const GOOGLE_TRANSLATE_DOM_GUARD_FLAG = "__googleTranslateDomGuardInstalled__";
const GOOGLE_TRANSLATE_STORAGE_KEY = "user_lang";
const GOOGLE_TRANSLATE_COOKIE_NAME = "googtrans";
const GOOGLE_TRANSLATE_BANNER_SELECTORS = [
  "iframe.goog-te-banner-frame",
  ".goog-te-banner-frame",
  "body > iframe.skiptranslate",
  "body > .skiptranslate",
  ".goog-te-ftab",
  ".VIpgJd-ZVi9od-ORHb",
  ".VIpgJd-ZVi9od-aZ2wEe-wOHMyf",
].join(", ");

const shouldSkipGoogleTranslateScript = () =>
  process.env.NODE_ENV === "development" &&
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

const installGoogleTranslateDomGuards = () => {
  if (
    typeof window === "undefined" ||
    typeof Node === "undefined" ||
    window[GOOGLE_TRANSLATE_DOM_GUARD_FLAG]
  ) {
    return;
  }

  const { removeChild, insertBefore, replaceChild } = Node.prototype;

  Node.prototype.removeChild = function removeChildGuard(child) {
    if (child && child.parentNode !== this) {
      return child;
    }

    try {
      return removeChild.call(this, child);
    } catch (error) {
      if (error?.name === "NotFoundError") {
        return child;
      }

      throw error;
    }
  };

  Node.prototype.insertBefore = function insertBeforeGuard(
    newNode,
    referenceNode
  ) {
    if (referenceNode && referenceNode.parentNode !== this) {
      return this.appendChild(newNode);
    }

    try {
      return insertBefore.call(this, newNode, referenceNode);
    } catch (error) {
      if (error?.name === "NotFoundError") {
        return this.appendChild(newNode);
      }

      throw error;
    }
  };

  Node.prototype.replaceChild = function replaceChildGuard(newChild, oldChild) {
    if (oldChild && oldChild.parentNode !== this) {
      return oldChild;
    }

    try {
      return replaceChild.call(this, newChild, oldChild);
    } catch (error) {
      if (error?.name === "NotFoundError") {
        return oldChild;
      }

      throw error;
    }
  };

  window[GOOGLE_TRANSLATE_DOM_GUARD_FLAG] = true;
};

const getSavedGoogleTranslateLanguage = () => {
  if (typeof window === "undefined") {
    return "en";
  }

  return window.localStorage.getItem(GOOGLE_TRANSLATE_STORAGE_KEY) || "en";
};

const syncGoogleTranslateCookie = (languageCode) => {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return;
  }

  const cookieValue = `/en/${languageCode}`;
  const cookieSettings = [
    `${GOOGLE_TRANSLATE_COOKIE_NAME}=${cookieValue};path=/`,
    `${GOOGLE_TRANSLATE_COOKIE_NAME}=${cookieValue};path=/;SameSite=Lax`,
  ];

  const hostname = window.location.hostname;
  if (hostname && hostname !== "localhost") {
    cookieSettings.push(
      `${GOOGLE_TRANSLATE_COOKIE_NAME}=${cookieValue};path=/;domain=${hostname};SameSite=Lax`
    );
    cookieSettings.push(
      `${GOOGLE_TRANSLATE_COOKIE_NAME}=${cookieValue};path=/;domain=.${hostname};SameSite=Lax`
    );
  }

  cookieSettings.forEach((cookie) => {
    document.cookie = cookie;
  });
};

const applySavedGoogleTranslateLanguage = () => {
  if (typeof document === "undefined") {
    return false;
  }

  const savedLanguage = getSavedGoogleTranslateLanguage();
  syncGoogleTranslateCookie(savedLanguage);

  const combo = document.querySelector(".goog-te-combo");
  if (!combo) {
    return false;
  }

  combo.value = savedLanguage;
  combo.dispatchEvent(new Event("change", { bubbles: true }));
  document.documentElement.lang = savedLanguage;
  return true;
};

const useGoogleTranslate = ({
  elementId = "google_translate_element",
  pageLanguage = "en",
  includedLanguages,
} = {}) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    installGoogleTranslateDomGuards();
    syncGoogleTranslateCookie(getSavedGoogleTranslateLanguage());

    if (shouldSkipGoogleTranslateScript()) {
      setIsReady(false);
      return undefined;
    }

    let cancelled = false;
    let retryTimeoutId = null;
    let bannerSweepIntervalId = null;
    let bannerObserver = null;
    let injectedScript = null;
    let existingScript = null;

    const sweepGoogleTranslateBanner = () => {
      const banners = document.querySelectorAll(
        GOOGLE_TRANSLATE_BANNER_SELECTORS
      );

      banners.forEach((banner) => {
        banner.style.setProperty("display", "none", "important");
        banner.style.setProperty("visibility", "hidden", "important");
        banner.style.setProperty("opacity", "0", "important");
        banner.style.setProperty("height", "0px", "important");
        banner.style.setProperty("min-height", "0px", "important");
        banner.style.setProperty("max-height", "0px", "important");
        banner.style.setProperty("width", "0px", "important");
        banner.style.setProperty("border", "0", "important");
        banner.style.setProperty("box-shadow", "none", "important");
        banner.style.setProperty("pointer-events", "none", "important");
        banner.style.setProperty("overflow", "hidden", "important");
      });

      document.body.style.setProperty("position", "static", "important");
      document.body.style.setProperty("top", "0px", "important");
      document.body.style.setProperty("margin-top", "0px", "important");
      document.body.style.setProperty("transform", "none", "important");

      document.documentElement.style.setProperty(
        "position",
        "static",
        "important"
      );
      document.documentElement.style.setProperty("top", "0px", "important");
      document.documentElement.style.setProperty(
        "margin-top",
        "0px",
        "important"
      );
      document.documentElement.style.setProperty(
        "transform",
        "none",
        "important"
      );
    };

    const clearRetry = () => {
      if (retryTimeoutId) {
        window.clearTimeout(retryTimeoutId);
      }
    };

    const waitForGoogleCombo = (remainingAttempts = 40) => {
      if (cancelled) {
        return;
      }

      const combo = document.querySelector(".goog-te-combo");
      if (combo) {
        applySavedGoogleTranslateLanguage();
        setIsReady(true);
        return;
      }

      if (remainingAttempts <= 0) {
        return;
      }

      retryTimeoutId = window.setTimeout(
        () => waitForGoogleCombo(remainingAttempts - 1),
        250
      );
    };

    const initializeWidget = () => {
      if (cancelled || !window.google?.translate?.TranslateElement) {
        return;
      }

      const container = document.getElementById(elementId);
      if (!container) {
        return;
      }

      container.innerHTML = "";
      setIsReady(false);

      const widgetConfig = {
        pageLanguage,
        autoDisplay: false,
        multilanguagePage: true,
      };

      if (includedLanguages) {
        widgetConfig.includedLanguages = includedLanguages;
      }

      new window.google.translate.TranslateElement(widgetConfig, elementId);

      waitForGoogleCombo();
    };

    const handleScriptError = () => {
      if (!cancelled) {
        setIsReady(false);
      }
    };

    sweepGoogleTranslateBanner();
    bannerSweepIntervalId = window.setInterval(sweepGoogleTranslateBanner, 500);

    if (typeof MutationObserver !== "undefined" && document.body) {
      bannerObserver = new MutationObserver(() => {
        sweepGoogleTranslateBanner();
      });

      bannerObserver.observe(document.body, {
        childList: true,
      });
    }

    const previousInit = window.googleTranslateElementInit;
    const currentInit = () => {
      if (typeof previousInit === "function" && previousInit !== currentInit) {
        previousInit();
      }

      initializeWidget();
    };

    window.googleTranslateElementInit = currentInit;

    if (window.google?.translate?.TranslateElement) {
      initializeWidget();
    } else {
      existingScript = document.getElementById(GOOGLE_TRANSLATE_SCRIPT_ID);

      if (existingScript) {
        existingScript.addEventListener("load", initializeWidget);
        existingScript.addEventListener("error", handleScriptError);
      } else {
        injectedScript = document.createElement("script");
        injectedScript.id = GOOGLE_TRANSLATE_SCRIPT_ID;
        injectedScript.src =
          "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
        injectedScript.async = true;
        injectedScript.defer = true;
        injectedScript.onerror = handleScriptError;
        document.body.appendChild(injectedScript);
      }
    }

    return () => {
      cancelled = true;
      clearRetry();
      if (bannerSweepIntervalId) {
        window.clearInterval(bannerSweepIntervalId);
      }
      if (bannerObserver) {
        bannerObserver.disconnect();
      }

      if (existingScript) {
        existingScript.removeEventListener("load", initializeWidget);
        existingScript.removeEventListener("error", handleScriptError);
      }

      if (window.googleTranslateElementInit === currentInit) {
        if (typeof previousInit === "function") {
          window.googleTranslateElementInit = previousInit;
        } else {
          delete window.googleTranslateElementInit;
        }
      }

      if (injectedScript?.parentNode) {
        injectedScript.parentNode.removeChild(injectedScript);
      }
    };
  }, [elementId, includedLanguages, pageLanguage]);

  return { isReady };
};

export default useGoogleTranslate;
